import React, { useState } from "react";
import NavLogo from "../assets/InnaxysLogo.svg";
import { TbCaretDownFilled, TbWorld } from "react-icons/tb";
import { useNavigate, useLocation } from "react-router-dom";
import LanguageModal from "../components/LanguageModal.jsx";
import { useLanguage } from "../config/LanguageContext.jsx";
import NavDropdownModal from "./navDropdownModal.jsx";
import navItems from "../translation/nav/nav.json";
import {
  IconBuildingSkyscraper,
  IconFlame,
  IconPlane,
  IconSchool,
  IconShield,
} from "@tabler/icons-react";

const Nav = () => {
  const { currentLanguage, handleLanguageChange } = useLanguage();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isActivePath = (paths) => {
    for (const path of paths) {
      if (location.pathname === path) return true;
    }
    return false;
  };

  const toggleLanguageModal = () => {
    setShowLanguageModal(!showLanguageModal);
  };

  const iconMap = {
    IconUserShield: IconShield,
    IconFlame: IconFlame,
    IconBuildingSkyscraper: IconBuildingSkyscraper,
    IconPlane: IconPlane,
    IconSchool: IconSchool,
  };

  const handleNavItemClick = (path) => {
    navigate(path);
  };

  const handleDropdownItemClick = (path) => {
    const [basePath, hash] = path.split("#");

    if (location.pathname === basePath && hash) {
      setTimeout(() => {
        const section = document.getElementById(hash);
        if (section) {
          const yOffset = -100;
          const y =
            section.getBoundingClientRect().top + window.scrollY + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    } else {
      navigate(basePath);
      setTimeout(() => {
        const section = document.getElementById(hash);
        if (section) {
          const yOffset = -100;
          const y =
            section.getBoundingClientRect().top + window.scrollY + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 500);
    }
  };

  const handleBookDemoClick = () => {
    navigate("/bookdemo");
  };

  const toggleDropdown = () => {
    setShowMenu(false);
    setShowDropdown(!showDropdown);
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === "Enter") {
      setShowDropdown(true);
    }
  };

  const handleKeyDown = (event, path) => {
    if (event.key === "Enter") {
      navigate(path);
    }
  };
  

  return (
    <section className="navbar-wrapper">
      <div className="navbar">
        <figure className="innaxys-logo">
          <a href="/">
            <img src={NavLogo} alt="Nav Logo" />
          </a>
        </figure>
        <div className="nav-options-container">
          <ul className="nav-options">
            {navItems.map((item, index) => (
              <li
                className={`nav-option ${
                  isActivePath([
                    item.path,
                    ...item.dropdownSections.map((section) =>
                      section.items.map((i) => i.path)
                    ),
                  ])
                    ? "active-nav"
                    : ""
                }`}
                key={index}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
                onFocus={() => setHoveredItem(index)}
              >
                <div
                  className="nav-item-container"
                  onClick={() => handleNavItemClick(item.path)}
                  onKeyDown={(event) => handleKeyDown(event, item.path)}
                  tabIndex="0"
                >
                  {item.name[currentLanguage]}
                  <span className="nav-caret">
                    <TbCaretDownFilled className="caret" size={20} />
                  </span>
                </div>

                {/* Hovered Item Dropdown */}
                {hoveredItem === index && (
                  <div
                    className={`desktop-nav-dropdown-container desktop-nav-dropdown-container-${item.className
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <ul className="desktop-nav-dropdown">
                      {item.dropdownSections.map((section, sectionIndex) => (
                        <li key={sectionIndex}>
                          {section.header && (
                            <div className="dropdown-header">
                              {section.header[currentLanguage]}
                            </div>
                          )}
                          {section.items.map((dropdownItem, i) => {
                            const IconComponent = iconMap[dropdownItem.icon];
                            const hasVisibleItems =
                              dropdownItem.subitems?.length > 0 ||
                              dropdownItem.path;

                            const isIndustriesItem = item.className === "Industries";

                            if (!hasVisibleItems) return null;

                            return (
                              <li
                                className={`desktop-dropdown-nav-option ${
                                  location.pathname === dropdownItem.path
                                    ? "active-dropdown-item"
                                    : ""
                                } 
                                ${
                                  isIndustriesItem ? "nav-item-industries" : ""
                                } dropdown-item-${dropdownItem.label[
                                  currentLanguage
                                ]
                                  .toLowerCase()
                                  .replace(/\s+/g, "-")}`}
                                key={i}
                                onClick={() =>
                                  handleDropdownItemClick(
                                    dropdownItem.link || dropdownItem.path
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleKeyDown(
                                    event,
                                    dropdownItem.link || dropdownItem.path
                                  )
                                }
                                tabIndex="0"
                              >
                                <div className="dropdown-subheader-container">
                                  {IconComponent && <IconComponent size={26} />}
                                  <span>
                                    {dropdownItem.label[currentLanguage]}
                                  </span>
                                  {dropdownItem.description && (
                                    <p className="solution-description">
                                      {
                                        dropdownItem.description[
                                          currentLanguage
                                        ]
                                      }
                                    </p>
                                  )}
                                </div>

                                {dropdownItem.subitems && (
                                  <ul>
                                    {dropdownItem.subitems.map(
                                      (subItem, subIndex) => {
                                        const label =
                                          subItem.label[currentLanguage]; // Ensure you use the language-specific label
                                        return (
                                          <li
                                            key={subIndex}
                                            onClick={() =>
                                              handleDropdownItemClick(
                                                subItem.link
                                              )
                                            }
                                            onKeyDown={(event) =>
                                              handleKeyDown(event, subItem.link)
                                            }
                                            className={`sub-dropdown-item sub-dropdown-item-${label
                                              .toLowerCase()
                                              .replace(/\s+/g, "-")}`} // Use the language-specific label
                                            tabIndex="0"
                                          >
                                            {label}{" "}
                                            {/* Render the translated label */}
                                          </li>
                                        );
                                      }
                                    )}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="nav-right-side">
          {/* <button className="nav-lang-button" onClick={toggleLanguageModal}>
            <TbWorld size={20} />
          </button> */}
          <button
            onClick={handleBookDemoClick}
            className={`cta-button nav-button ${showMenu ? "hidden" : ""}`}
          >
            {
              {
                en: "Request a demo",
                fr: "Réserver une démo",
                de: "Demo anfordern",
                es: "Solicitar una demo",
              }[currentLanguage]
            }
          </button>

          <i
            className="hamburger-menu nav-icon"
            onClick={toggleDropdown}
            onKeyDown={handleMenuKeyDown}
            tabIndex="0"
          >
            <div id="nav-icon3">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </i>
        </div>
      </div>

      {/* Overlay for the dropdown */}
      <div
        className={`overlay ${showDropdown ? "visible" : ""}`}
        onClick={() => setShowDropdown(false)}
      ></div>

      {showDropdown && (
        <NavDropdownModal
          onClose={() => setShowDropdown(false)}
          handleNavItemClick={handleNavItemClick}
          handleBookDemoClick={handleBookDemoClick}
        />
      )}

      {/* Render the LanguageModal */}
      {showLanguageModal && (
        <LanguageModal
          currentLanguage={currentLanguage}
          handleLanguageChange={handleLanguageChange}
          toggleLanguageModal={toggleLanguageModal}
        />
      )}
    </section>
  );
};

export default Nav;
